import React, {
   MutableRefObject,
   ReactNode,
   useCallback,
   useRef,
} from 'react';
import { getEnvironment, ModalHandle, trackModalClose, trackModalOpen } from '@unifocus/js-utils';
import ReactModal from 'react-modal';
import { ModalSize } from './Modal';

export type ModalContainerSize = 'small' | 'medium' | 'large' | 'fullscreen';

export interface ModalContainerProps {
   visible?: boolean;
   /**
    * If this is set, it enables analytics pageviews for the modal.  Set this to the simple name
    * of the Modal using all lowercase with dashes to separate words.  For example:
    * approve-employee-request.
    */
   analyticsName?: string;
   /**
    * Specifies size for the modal.
    *
    * @default 'small'
    */
   size?: ModalContainerSize;
   shouldCloseOnEsc?: boolean;
   shouldCloseOnMaskClick?: boolean;
   onClose?: () => void;
   onAfterClose?: () => void;
   onAfterOpen?: () => void;
   className?: string;
   style?: React.CSSProperties;
   children?: ReactNode;
}

const overlayClassName = {
   base: 'uf-modal-overlay',
   afterOpen: 'uf-modal-overlay--after-open',
   beforeClose: 'uf-modal-overlay--before-close'
};

export const ModalContainer = (props: ModalContainerProps) => {
   const modalHandleRef = useRef<ModalHandle | null>(null);
   const analyticsName = props.analyticsName;
   const className = getClassNameBasedOnSize(props.size, props.className);

   if (analyticsName !== undefined) {
      if (props.visible === true && modalHandleRef.current === null) {
         modalHandleRef.current = trackModalOpen(getAnalyticsPath(analyticsName));
      }
   }

   const style = props.style || {};

   const afterCloseCallback = useCallback(() => {
      return afterClose(modalHandleRef, props.onAfterClose, analyticsName);
   },[props.onAfterClose, analyticsName, modalHandleRef]);

   return (
      <ReactModal
         isOpen={props.visible === undefined ? false : props.visible}
         overlayClassName={overlayClassName}
         className={{
            base: `uf-modal-content ${className}`,
            afterOpen: 'uf-modal-content--after-open',
            beforeClose: 'uf-modal-content--before-close'
         }}
         style={style ? {content: style} : undefined}
         appElement={document.querySelector('#root') || {}}
         shouldCloseOnEsc={props.shouldCloseOnEsc}
         shouldCloseOnOverlayClick={props.shouldCloseOnMaskClick === undefined ? false : props.shouldCloseOnMaskClick}
         onAfterClose={afterCloseCallback}
         onAfterOpen={props.onAfterOpen}
         onRequestClose={props.onClose}
      >
         {props.children}
      </ReactModal>
   );
};

const afterClose = (modalHandleRef: MutableRefObject<ModalHandle | null>, onAfterClose?: () => void, analyticsName?: string) => {

   if (analyticsName === undefined) {
      return onAfterClose;
   }

   return () => {
      if (modalHandleRef.current !== null) {
         trackModalClose(modalHandleRef.current);
         modalHandleRef.current = null;
      }

      if (onAfterClose) {
         onAfterClose();
      }
   };
};

const getAnalyticsPath = (analyticsName: string) => {
   const uri = `${window.location.pathname}${window.location.search}${window.location.hash}`;
   const separatorChar = uri.endsWith('/') ? '' : '/';
   return `${uri}${separatorChar}modal/${analyticsName}`;
};

const defaultModalSize: ModalSize = 'small';
const defaultModalSizeMobile: ModalSize = 'fullscreen';

const getClassNameBasedOnSize = (size?: ModalSize, className?: string) => {
   const modalSize = size || (getEnvironment().mobile ? defaultModalSizeMobile : defaultModalSize);
   const classes = [`uf-modal-content--${modalSize}`];

   if (className) {
      classes.push(className);
   }

   return classes.join(' ');
};
